import {
  Archive,
  ArchiveRestore,
  CopyPlus,
  EditIcon,
  Trash2,
} from 'lucide-react'

import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  RowActionsMenu,
} from '@/components/ui'
import type { CellContext } from '@/components/ui/table/types'
import {
  allowsManyProjects,
  useAccount,
  useIsTrialAccount,
} from '@/modules/account'
import { DeleteProjectsDialog } from '@/modules/projects/components/dialogs/DeleteProjectsDialog'
import { EditProjectDialog } from '@/modules/projects/components/dialogs/EditProjectDialog'
import { isProjectDeletable } from '@/modules/projects/expiration'
import { isProjectBusy } from '@/modules/projects/status'
import type { Project } from '@/modules/projects/stores/types'
import { useDialogActions } from '@/stores/dialog'
import { archiveProjects, unarchiveProjects } from './archiveProjects'
import { duplicateProject } from './duplicateProject'

export const RowActionsCell = (cell: CellContext<Project, unknown>) => {
  const { openDialog } = useDialogActions()
  const account = useAccount()
  const isTrial = useIsTrialAccount()
  const project = cell.row.original

  return (
    <RowActionsMenu label="Project actions">
      {project.archivedAt ? (
        <DropdownMenuItem onClick={() => unarchiveProjects([project])}>
          <ArchiveRestore className="size-4" />
          Unarchive project
        </DropdownMenuItem>
      ) : (
        <>
          <DropdownMenuItem
            onClick={() => openDialog(EditProjectDialog.id, project)}
          >
            <EditIcon className="size-4" />
            Edit project
          </DropdownMenuItem>
          {allowsManyProjects(account?.planKind) && (
            <DropdownMenuItem
              onClick={() => duplicateProject(project.slug)}
              disabled={isProjectBusy(project)}
            >
              <CopyPlus className="size-4" />
              Duplicate project
            </DropdownMenuItem>
          )}
          {!isTrial && (
            <DropdownMenuItem
              onClick={() => archiveProjects([project])}
              disabled={isProjectBusy(project)}
            >
              <Archive className="size-4" />
              Archive project
            </DropdownMenuItem>
          )}
        </>
      )}
      {isProjectDeletable(project, account?.planKind) && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => openDialog(DeleteProjectsDialog.id, [project])}
            destructive
            disabled={isProjectBusy(project)}
          >
            <Trash2 className="size-4" />
            Delete project
          </DropdownMenuItem>
        </>
      )}
    </RowActionsMenu>
  )
}
